import { guardedClient as clientRequest } from '@repo/shared/helpers';
import {
	sitesApiResponseSchema,
	siteSchema,
	siteOverviewSchema,
	hasReviewsAndApprovalsSchema,
	siteAttendeesApiResponseSchema,
	zonesApiResponseSchema,
	taskTemplateListSchema,
	Action,
	UsersApiResponse,
	GroupsApiResponse,
	EventsApiResponse,
	Site,
	SitesApiResponse,
} from '@repo/shared/domain';

export const getSelectedSite = async () => {
	const { body: selectedSite } = await clientRequest.get('/api/users/selected-site', { schema: siteSchema });
	return selectedSite;
};

export const createSite = async (): Promise<Site> => {
	return clientRequest.post('/api/sites/', { schema: siteSchema });
};

export const updateSite = async (siteId: string, data: Partial<Site>): Promise<Site> => {
	return clientRequest.put(`/api/sites/${siteId}`, data);
};

// Sites (system admin)
export const getSites = async (query?: string): Promise<SitesApiResponse> => {
	const { body } = await clientRequest.get(`/api/sites${query ? `?${query}` : ''}`, {
		schema: sitesApiResponseSchema,
	});
	return body;
};

export const getAllSites = async (): Promise<SitesApiResponse> => {
	const { body } = await clientRequest.get('/api/sites?noPagination=true', {
		schema: sitesApiResponseSchema,
	});
	return body;
};

export const getSite = async (siteId: string) => {
	const { body: site } = await clientRequest.get(`/api/sites/${siteId}`, { schema: siteSchema });
	return site;
};

export const getSiteOverview = async (siteId: string) => {
	const { body: overview } = await clientRequest.get(`/api/sites/${siteId}/overview`, {
		schema: siteOverviewSchema,
	});
	return overview;
};

export const getRemoteActions = async (siteId: string) => {
	const { body: remoteActions } = await clientRequest.get<undefined, Action[]>(`/api/sites/${siteId}/remote-actions`, {
		schema: undefined,
	});
	return remoteActions;
};

export const hasReviewsAndApprovals = async (siteId: string) => {
	const { body: result } = await clientRequest.get(`/api/sites/${siteId}/has-reviews-and-approvals`, {
		schema: hasReviewsAndApprovalsSchema,
	});
	return result;
};

export const getSiteAttendees = async (siteId: string, query?: string) => {
	const { body: attendees } = await clientRequest.get(
		`/api/sites/${siteId}/overview/attendees${query ? `?${query}` : ''}`,
		{ schema: siteAttendeesApiResponseSchema },
	);
	return attendees;
};

export const getZones = async (query?: string) => {
	const baseQuery = query?.includes('noPagination=') ? '' : 'noPagination=true';
	const fullQuery = [baseQuery, query].filter(Boolean).join('&');
	const { body: zones } = await clientRequest.get(`/api/zones${fullQuery ? `?${fullQuery}` : ''}`, {
		schema: zonesApiResponseSchema,
	});
	return zones;
};

export const getApprovals = async (forApprover: boolean, query?: string) => {
	const { body: approvals } = await clientRequest.get(
		`/api/tasks/approvals/${forApprover ? 'for-approver' : 'view-only'}${query ? `?${query}` : ''}`,
		{ schema: taskTemplateListSchema },
	);
	return approvals;
};

export const getUsersInReview = async (query?: string) => {
	const { body: users } = await clientRequest.get<undefined, UsersApiResponse>(
		`/api/users/marked-for-review${query ? `?${query}` : ''}`,
		{ schema: undefined },
	);
	return users;
};

export const getGroupsInReview = async (query?: string) => {
	const { body: groups } = await clientRequest.get<undefined, GroupsApiResponse>(
		`/api/groups/marked-for-review${query ? `?${query}` : ''}`,
		{ schema: undefined },
	);
	return groups;
};

export const getEventsInReview = async (query?: string) => {
	const { body: events } = await clientRequest.get<undefined, EventsApiResponse>(
		`/api/events/marked-for-review${query ? `?${query}` : ''}`,
		{ schema: undefined },
	);
	return events;
};
